import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { component, ObjectManager, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, PotentialInteraction } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Resizable } from '../../canvas/entities/Resizable';
import { Snappable } from '../../canvas/entities/Snappable';
import { UIField } from '../../canvas/entities/UIField';
import { ComputeController } from './ComputeController';
import { SmartButtonLabelField } from './SmartButtonLabelField';
import { smartButtonPropertyLayout } from './SmartButtonPropertyLayout';

const invocation = defineCommand(
    'smartSmartButtonInvoke',
    null,
    (cc, [id]: [string]) => {

        const objects = cc.resolve(ObjectManager);
        const compute = cc.resolve(ComputeController);
        const self = objects.get(id, SmartButton);

        compute.execute(self, 'active');

    },
    [],
);

const mutation = defineCommand(
    'buttonMutate',
    'SmartButton Mutate',
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const smartButtonDef = {
    name: 'SmartButton',
    implements: [Physical, Resizable, Snappable],
    invocation,
    mutation,
    propertyLayout: smartButtonPropertyLayout
};

@component(smartButtonDef)
export class SmartButton extends RectangularPhysical implements Snappable {

    @property({ type: 'string', default: '' })
    public label!: string;

    @property({ type: 'string', default: '#dfdfdf', style: true })
    public background!: string;

    public get boundary() {
        return true;
    }

    public get minWidth() {
        return 20;
    }

    public get minHeight() {
        return 20;
    }

    @memoUsing('width', 'height')
    public get layout() {
        const base = this.localBounds;
        const inner = base.set({ height: base.height - 2 });
        const label = inner.inflate(-10);
        return { base, inner, label };
    }

    @memoUsing('layout')
    public get uiFields(): readonly UIField[] {
        return [SmartButtonLabelField(this)];
    }

    public get shouldSnap() {
        return true;
    }

    public testInteraction({ type, mpt, selected }: PotentialInteraction): [boolean, string] {
        if (this.localBounds.contains(this.toRelative(mpt))) {
            if (type !== 'invoke' || !selected) {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }

    public toHumanString(): string {
        return `SmartButton ("${this.label}")`;
    }
}
